import React, { useState, useMemo, useEffect } from 'react'
import { gospelTerms } from '../utils/gospelTerms'

interface GospelSymbolsModalProps {
	isOpen: boolean;
	onClose: () => void;
}

export const GospelSymbolsModal: React.FC<GospelSymbolsModalProps> = ({ isOpen, onClose }) => {
	const [searchFilter, setSearchFilter] = useState('')
	const [copiedTerm, setCopiedTerm] = useState<string | null>(null)

	useEffect(() => {
		const keyHandler = (e: KeyboardEvent) => {
			if (e.key === 'Escape') {
				onClose()
			}
		}

		document.addEventListener('keydown', keyHandler)

		return () => document.removeEventListener('keydown', keyHandler)
	}, [])

	const filteredTerms = useMemo(() => {
		if (!searchFilter.trim()) {
			return Object.values(gospelTerms)
		}

		const filter = searchFilter.toLowerCase()
		return Object.values(gospelTerms).filter(term =>
			term.label.toLowerCase().includes(filter) ||
            term.term.toLowerCase().includes(filter),
		)
	}, [searchFilter])

	const handleCopyTerm = async (term: string) => {
		try {
			await navigator.clipboard.writeText(term)
			setCopiedTerm(term)
			// Clear the toast after 2 seconds
			setTimeout(() => setCopiedTerm(null), 2000)
		} catch (err) {
			console.error('Failed to copy term:', err)
		}
	}

	if (!isOpen) return null

	return (
		<div className="modal-overlay" onClick={onClose}>
			<div className="gospel-symbols-modal" onClick={(e) => e.stopPropagation()}>
				<div className="help-header">
					<h2>Gospel Symbols Reference</h2>
					<button className="close-button" onClick={onClose}>×</button>
				</div>

				<div className="gospel-symbols-content">
					<div className="search-section">
						<input
							type="text"
							placeholder="Search symbols..."
							value={searchFilter}
							onChange={(e) => setSearchFilter(e.target.value)}
							className="symbol-search-input"
							autoFocus
						/>
					</div>

					<div className="symbols-grid">
						{filteredTerms.map((term) => (
							<div
								key={term.term}
								className="symbol-item symbol-item--clickable"
								onClick={() => handleCopyTerm(`(!${term.term})`)}
								title={`Click to copy "(!${term.term})" to clipboard`}
							>
								<div className="symbol-thumbnail">
									<img
										src={term.src}
										srcSet={term.srcset}
										sizes={term.sizes}
										alt={term.label}
										loading="lazy"
									/>
								</div>
								<div className="symbol-info">
									<div className="symbol-label">{term.label}</div>
									<div className="symbol-term">(!{term.term})</div>
								</div>
							</div>
						))}
					</div>

					{filteredTerms.length === 0 && (
						<div className="no-results">
							No symbols found matching "{searchFilter}"
						</div>
					)}

					{copiedTerm && (
						<div className="copy-toast">
							Copied "{copiedTerm}" to clipboard!
						</div>
					)}
				</div>

				<div className="help-footer">
					<small className="gray-note">
						Click a word to copy a symbol, then paste it into the lyrics. {}
						List from <a href="https://www.churchofjesuschrist.org/study/manual/gospel-symbols">churchofjesuschrist.org/study/manual/gospel-symbols</a>
					</small>

					{/* <button className="primary-button" onClick={onClose}>
						Close
					</button> */}
				</div>
			</div>
		</div>
	)
}
